import { For, createMemo } from 'solid-js'
import { Portal } from 'solid-js/web'
import { BUILTIN_FONTS, collectUsedFamilies, type FontContext } from './fonts'
import type { ThemeRecord } from '../project/themeVariables'
import type { ObjectContext } from '../project/objects'

// Injects Google Fonts <link> tags for all font families referenced in the
// object tree or explicitly preloaded.  SolidJS manages the DOM elements
// declaratively — <For> mounts a <link> when a family is first needed and
// removes it if it is no longer needed (browser-cached fonts are retained
// in memory even after their <link> is removed, so there is no flash).
//
// Strategy:
//   • Imported fonts (fontCtx.fonts) — always loaded immediately so they are
//     ready before they are applied to an object (background preload on initial
//     load and whenever a new font is added via the font manager).
//   • Built-in fonts — only loaded on demand (when actually used in an object
//     or when a user hovers/selects them), keeping the initial bundle small.
export function FontLoader(P: { objects: ObjectContext, fonts: FontContext, theme: ThemeRecord|undefined })
{
	const objCtx  = P.objects
	const fontCtx = P.fonts

	const fontsToLoad = createMemo(() => {
		// Imported fonts are always preloaded regardless of object usage.
		const importedSpecs = new Set(fontCtx.fonts())

		// Built-in fonts are loaded only when referenced in objects or explicitly
		// requested (e.g. while the font-family picker is open).
		const usedFamilies = collectUsedFamilies(objCtx.objects(), P.theme)
		for (const f of fontCtx.preloadFamilies()) usedFamilies.add(f)
		const usedBuiltins = BUILTIN_FONTS.filter(spec => {
			const family = spec.split(':')[0].replace(/\+/g, ' ')
			return usedFamilies.has(family)
		})

		// Merge — imported specs take precedence; built-ins fill in the rest.
		return [...new Set([...importedSpecs, ...usedBuiltins])]
	})

	return (
		<Portal mount={document.head}>
			<For each={fontsToLoad()}>{spec =>
				<link
					rel="stylesheet"
					href={`https://fonts.googleapis.com/css2?family=${spec}&display=swap`}
				/>
			}</For>
		</Portal>
	)
}
